import {
  FC,
  HTMLProps,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useCacheCgdo } from 'app/shared/queries/useCacheCgdo';
import { computeDlHashIdForCgdo } from 'app/shared/utils/dlHashId';

import {
  ENTITY_SEARCH_TYPE_OPTIONS,
  OLD_TO_NEW_SEARCH_TYPE_MAP,
} from 'app/modules/search/constants';
import { INFINITE_SCROLL_LIMIT, OFFSET_DEFAULT } from 'app/shared/constants';
import styled from 'styled-components';

import { entitiesSearch } from 'app/modules/search/actions';
import { formatEntityOptions, ValueField } from 'app/modules/search/helpers';
import {
  selectEntitiesSearchResults,
  selectEntitiesSearchLoading,
} from 'app/modules/search/selectors';
import { useSelector, useDispatch } from 'react-redux';

import { IconBuilding, IconSearch, IconUser } from '@u21/tabler-icons';
import {
  U21InputGroup,
  U21Select,
  U21SelectSearch,
  U21SelectProps,
  U21SelectSearchMultiProps,
} from 'app/shared/u21-ui/components';
import { useLocalStorageState } from 'app/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'app/shared/constants/localStorage';
import {
  selectEntityPhoneNumberSearchV2Enabled,
  selectSearchV2Enabled,
  selectSearchV2UseDlHashIdEnabled,
} from 'app/shared/featureFlags/selectors';
import { EntitySearchPayload } from 'app/modules/search/payload';
import {
  searchEntity,
  useEntitySearch,
} from 'app/modules/entities/queries/useEntitySearch';
import { useQueryClient } from '@tanstack/react-query';
import { ENTITY_QUERY_KEYS } from 'app/modules/entities/queries/keys';
import {
  EntitySearchOptions,
  OldEntitySearchType,
} from 'app/modules/search/enum';
import { getEntitySearchConditions } from 'app/modules/search/utils';
import {
  selectAgentOrgID,
  selectHasReadEntitiesPermissions,
} from 'app/modules/session/selectors';

export interface U21EntitySearchProps extends Omit<
  HTMLProps<HTMLDivElement>,
  'as' | 'ref' | 'onChange' | 'value'
> {
  disabled?: boolean;
  error?: boolean;
  clearable?: boolean;
  onChange: (value: (number | string)[], e: SyntheticEvent) => void;
  value?: (number | string)[];
  valueField?: ValueField;
  selectProps?: Omit<
    U21SelectProps<string, false>,
    'disabled' | 'error' | 'onChange' | 'options' | 'ref' | 'as'
  >;
  searchProps?: Omit<
    U21SelectSearchMultiProps<number>,
    | 'disabled'
    | 'error'
    | 'clearable'
    | 'onChange'
    | 'onSearch'
    | 'options'
    | 'ref'
    | 'value'
    | 'multi'
    | 'as'
  >;
}

export const U21EntitySearch: FC<U21EntitySearchProps> = ({
  disabled,
  error,
  clearable = true,
  onChange,
  value = [],
  valueField = ValueField.EXTERNAL_ID,
  selectProps = {},
  searchProps = {},
  ...rest
}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [newPayload, setNewPayload] = useState<EntitySearchPayload>();
  const [searchValue, setSearchValue] = useState<string>('');

  const isSearchV2Enabled = useSelector(selectSearchV2Enabled);
  const isSearchV2UseDLHashIdEnabled = useSelector(
    selectSearchV2UseDlHashIdEnabled,
  );
  const { data: v1Data } = useSelector(selectEntitiesSearchResults);
  const v1Loading = useSelector(selectEntitiesSearchLoading);
  const hasReadEntitiesPermissions = useSelector(
    selectHasReadEntitiesPermissions,
  );
  const isEntityPhoneNumberSearchV2Enabled = useSelector(
    selectEntityPhoneNumberSearchV2Enabled,
  );

  const [entityIdExactMatch] = useLocalStorageState<boolean>(
    LocalStorageKeys.SEARCH_ENTITY_ID_EXACT_MATCH,
    true,
  );

  const typeOptions = useMemo(
    () =>
      ENTITY_SEARCH_TYPE_OPTIONS.filter(
        (option) =>
          option.value !== EntitySearchOptions.PHONE_NUMBERS ||
          isEntityPhoneNumberSearchV2Enabled,
      ),
    [isEntityPhoneNumberSearchV2Enabled],
  );

  const [type, setType] = useState<OldEntitySearchType>(
    entityIdExactMatch
      ? OldEntitySearchType.EXACT_EXTERNAL_ID
      : OldEntitySearchType.EXTERNAL_ID,
  );

  const isV2SearchUsed = useMemo(
    () => Boolean(isSearchV2Enabled && OLD_TO_NEW_SEARCH_TYPE_MAP[type]),
    [isSearchV2Enabled, type],
  );

  const {
    entities,
    query: { isFetching },
    refreshEntities,
  } = useEntitySearch(newPayload, isV2SearchUsed);

  const data = useMemo(
    () => (isV2SearchUsed ? entities : v1Data),
    [entities, isV2SearchUsed, v1Data],
  );

  const loading = useMemo(
    () => (isV2SearchUsed ? isFetching : v1Loading),
    [isFetching, isV2SearchUsed, v1Loading],
  );

  // Reset types
  useEffect(() => {
    setType((prevType) => {
      if (
        prevType === OldEntitySearchType.EXTERNAL_ID ||
        prevType === OldEntitySearchType.EXACT_EXTERNAL_ID ||
        (prevType === OldEntitySearchType.PHONE_NUMBERS &&
          !isEntityPhoneNumberSearchV2Enabled)
      ) {
        return entityIdExactMatch
          ? OldEntitySearchType.EXACT_EXTERNAL_ID
          : OldEntitySearchType.EXTERNAL_ID;
      }

      return prevType;
    });
  }, [entityIdExactMatch, isEntityPhoneNumberSearchV2Enabled, typeOptions]);

  const options = useMemo(
    () =>
      formatEntityOptions(
        data,
        type,
        valueField,
        true,
        isSearchV2UseDLHashIdEnabled,
      ),
    [data, type, valueField, isSearchV2UseDLHashIdEnabled],
  );

  const orgId = useSelector(selectAgentOrgID);
  const { mutate: cacheCgdo } = useCacheCgdo();

  const wrappedOnChange = useCallback(
    (newValues: (number | string)[], e: SyntheticEvent) => {
      if (isV2SearchUsed && Array.isArray(newValues)) {
        const prevSet = new Set(value);
        newValues.forEach((val) => {
          if (prevSet.has(val)) return;
          // The form value is either the external_id (which we hash to the DL
          // hash_id) or the DL hash_id itself when the pg-cache flag is on.
          let hashedId: string | undefined;
          if (valueField === ValueField.EXTERNAL_ID) {
            hashedId = computeDlHashIdForCgdo(orgId, String(val));
          } else if (isSearchV2UseDLHashIdEnabled) {
            hashedId = String(val);
          }
          if (hashedId)
            cacheCgdo({ object_type: 'entity', hashed_id: hashedId });
        });
      }
      onChange(newValues, e);
    },
    [
      cacheCgdo,
      isSearchV2UseDLHashIdEnabled,
      isV2SearchUsed,
      onChange,
      orgId,
      value,
      valueField,
    ],
  );

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [value]);

  useEffect(() => {
    if (hasReadEntitiesPermissions) {
      const missingValues = value.filter(
        (i) => !options.find((j) => j.value === i),
      );

      if (missingValues.length && !calledRef.current) {
        calledRef.current = true;
        if (isV2SearchUsed) {
          const payload: EntitySearchPayload = {
            offset: OFFSET_DEFAULT,
            limit: missingValues.length,
            search_conditions: getEntitySearchConditions(
              missingValues.map(String),
              OldEntitySearchType.EXACT_EXTERNAL_ID,
            ),
            hydrate_unit21_id: valueField === ValueField.ID,
          };

          queryClient.prefetchQuery({
            queryKey: ENTITY_QUERY_KEYS.getEntitySearch(payload),
            queryFn: () => searchEntity(payload),
          });

          setNewPayload(payload);
        } else if (valueField === ValueField.ID) {
          dispatch(
            entitiesSearch({
              ids: missingValues,
              limit: missingValues.length,
              offset: OFFSET_DEFAULT,
              phrase: '',
              type: 'unit21_id',
            }),
          );
        } else {
          dispatch(
            entitiesSearch({
              external_ids: missingValues.map(String),
              limit: missingValues.length,
              offset: OFFSET_DEFAULT,
              phrase: '',
              type: 'external_id',
            }),
          );
        }
      }
    }
  }, [
    dispatch,
    hasReadEntitiesPermissions,
    isV2SearchUsed,
    options,
    queryClient,
    type,
    value,
    valueField,
  ]);

  const onSearch = useCallback(
    (query?: string) => {
      setSearchValue(query || '');

      if (query) {
        if (isV2SearchUsed) {
          setNewPayload({
            offset: OFFSET_DEFAULT,
            limit: INFINITE_SCROLL_LIMIT,
            search_conditions: getEntitySearchConditions([query], type),
            hydrate_unit21_id: valueField === ValueField.ID,
          });
        } else {
          dispatch(
            entitiesSearch({
              limit: INFINITE_SCROLL_LIMIT,
              offset: OFFSET_DEFAULT,
              phrase: query || '',
              type,
              ...(type === 'exact_external_id'
                ? {
                    type: 'external_id',
                    external_ids: (query || '')
                      .split(',')
                      .map((id) => id.trim()),
                  }
                : {}),
            }),
          );
        }
      }
    },
    [dispatch, isV2SearchUsed, type, valueField],
  );

  if (!hasReadEntitiesPermissions) {
    return null;
  }

  return (
    <U21InputGroup disabled={disabled} error={error}>
      <StyledU21Select
        clearable={false}
        disabled={disabled}
        error={error}
        label="Search Entity"
        onChange={(val: OldEntitySearchType) => setType(val)}
        options={typeOptions}
        searchable={false}
        startIcon={type === 'name' ? <IconBuilding /> : <IconUser />}
        value={type}
        {...selectProps}
      />
      <StyledU21SelectSearch
        disabled={disabled}
        error={error}
        clearable={clearable}
        limitTags={1}
        loading={loading}
        multi
        onChange={wrappedOnChange}
        onExactSearch={
          isV2SearchUsed
            ? async (searchTerms) => {
                const payload: EntitySearchPayload = {
                  offset: OFFSET_DEFAULT,
                  limit: searchTerms.length,
                  search_conditions: getEntitySearchConditions(
                    searchTerms.map(String),
                    OldEntitySearchType.EXACT_EXTERNAL_ID,
                  ),
                  hydrate_unit21_id: valueField === ValueField.ID,
                };

                await queryClient.prefetchQuery({
                  gcTime: Infinity,
                  queryKey: ENTITY_QUERY_KEYS.getEntitySearch(payload),
                  queryFn: () => searchEntity(payload),
                });
                refreshEntities();
              }
            : undefined
        }
        onSearch={onSearch}
        options={options}
        startIcon={<IconSearch />}
        value={value}
        noOptionsText={
          searchValue
            ? 'No results found'
            : 'Type to search or paste a list of Entity IDs'
        }
        {...rest}
        {...searchProps}
      />
    </U21InputGroup>
  );
};

const StyledU21Select = styled(U21Select)`
  min-width: 200px;
  width: 200px;
`;

const StyledU21SelectSearch = styled(U21SelectSearch)`
  min-width: 300px;
`;
